import {useCallback, useEffect, useState} from 'react'
import {Button} from '../../components/button/button.jsx'
import {disablePush, enablePush, getPermissionInfo, isPushSupported} from '../../pwa/push.js'
import {useToastStore} from '../../state/toast.js'
import styles from './notification-toggles.module.scss'

const COPY = {
    unsupported: {
        title: 'Notifications need an installed app',
        body: 'Add VerdaSync to your home screen, then enable notifications from inside the installed app.'
    },
    default: {
        title: 'Notifications are off',
        body: 'Turn on push alerts to be notified about system issues, weather warnings, and task reminders.'
    },
    grantedSubscribed: {
        title: 'Notifications are on',
        body: 'You will receive push alerts on this device.'
    },
    grantedUnsubscribed: {
        title: 'Notifications are off',
        body: 'Permission granted — tap Enable to start receiving alerts on this device.'
    },
    denied: {
        title: 'Notifications are blocked',
        body: 'Your browser has blocked notifications for this site. Enable them in browser settings to subscribe.'
    }
}

export function NotificationToggles() {
    const pushToast = useToastStore(state => state.pushToast)
    const [info, setInfo] = useState({supported: isPushSupported(), state: 'default', subscribed: false})
    const [busy, setBusy] = useState(false)

    const refresh = useCallback(async () => {
        const next = await getPermissionInfo()
        setInfo(next)
    }, [])

    useEffect(() => {
        refresh()
    }, [refresh])

    const onEnable = async () => {
        setBusy(true)
        try {
            await enablePush()
            pushToast({title: 'Notifications enabled', variant: 'info'})
        } catch (err) {
            pushToast({
                title: 'Could not enable notifications',
                body: err?.message || 'Please try again.',
                variant: 'alert'
            })
        } finally {
            setBusy(false)
            await refresh()
        }
    }

    const onDisable = async () => {
        setBusy(true)
        try {
            await disablePush()
            pushToast({title: 'Notifications disabled', variant: 'info'})
        } catch {
            pushToast({title: 'Could not disable notifications', variant: 'alert'})
        } finally {
            setBusy(false)
            await refresh()
        }
    }

    if (!info.supported) {
        return (
            <div className={styles.block}>
                <p className={styles.title}>{COPY.unsupported.title}</p>
                <p className={styles.body}>{COPY.unsupported.body}</p>
            </div>
        )
    }

    if (info.state === 'denied') {
        return (
            <div className={styles.block}>
                <p className={styles.title}>{COPY.denied.title}</p>
                <p className={styles.body}>{COPY.denied.body}</p>
            </div>
        )
    }

    const subscribed = info.state === 'granted' && info.subscribed
    const copy = subscribed
        ? COPY.grantedSubscribed
        : info.state === 'granted' ? COPY.grantedUnsubscribed : COPY.default

    return (
        <div className={styles.block}>
            <div className={styles.row}>
                <span className={styles.dot} data-on={subscribed || undefined} aria-hidden="true"/>
                <p className={styles.title}>{copy.title}</p>
            </div>
            <p className={styles.body}>{copy.body}</p>
            {subscribed ? (
                <Button variant="secondary" loading={busy} onClick={onDisable}>Disable notifications</Button>
            ) : (
                <Button variant="primary" loading={busy} onClick={onEnable}>Enable notifications</Button>
            )}
        </div>
    )
}
